import { useState } from 'react'
import { Outlet, Link, useLocation } from 'react-router-dom'
import {
  Home,
  Receipt,
  PieChart,
  Target,
  User,
  Sun,
  Moon,
  Menu,
  X,
  LogOut,
  HelpCircle
} from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { useTheme } from '../contexts/ThemeContext'
import AppLogo from './AppLogo'

function Layout() {
  const { user, logout } = useAuth()
  const { isDark, toggleTheme } = useTheme()
  const location = useLocation()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  const navigation = [
    { name: 'Home', href: '/home', icon: Home, shortName: 'Home' },
    { name: 'Transactions', href: '/transactions', icon: Receipt, shortName: 'Transactions' },
    { name: 'Analytics', href: '/analytics', icon: PieChart, shortName: 'Analytics' },
    { name: 'How App Works', href: '/how-app-works', icon: HelpCircle, shortName: 'How It Works' },
    { name: 'Budget', href: '/budget-planning', icon: Target, shortName: 'Budget' },
    { name: 'Profile', href: '/profile', icon: User, shortName: 'Profile' },
  ]

  // Split navigation for mobile
  const primaryNav = navigation.slice(0, 3) // Home, Transactions, Analytics
  const secondaryNav = navigation.slice(3) // Rest of the items

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 overflow-x-hidden">
      {/* Desktop Top Navigation */}
      <nav className="hidden lg:block bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center">
              <AppLogo size="small" showImage={true} />
            </div>

            {/* Desktop Navigation */}
            <div className="flex items-baseline space-x-4">
              {navigation.map((item) => {
                const Icon = item.icon
                const isActive = location.pathname === item.href

                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`
                      flex items-center space-x-2 px-3 py-2 rounded-xl text-sm font-medium transition-all duration-200
                      ${isActive
                        ? 'text-white shadow-brand'
                        : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
                      }
                    `}
                    style={isActive ? {
                      background: 'linear-gradient(135deg, #0891B2 0%, #059669 50%, #2563EB 100%)'
                    } : {}}
                  >
                    <Icon className="h-4 w-4" />
                    <span>{item.name}</span>
                  </Link>
                )
              })}
            </div>

            {/* Right side - User menu and theme toggle */}
            <div className="flex items-center space-x-4">
              <button
                onClick={toggleTheme}
                className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {isDark ? (
                  <Sun className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                ) : (
                  <Moon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                )}
              </button>

              {/* User dropdown */}
              <div className="flex items-center space-x-3">
                <div className="h-8 w-8 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-full flex items-center justify-center shadow-sm overflow-hidden">
                  {user?.profile_picture ? (
                    <img
                      src={user.profile_picture}
                      alt={user?.name || 'User'}
                      className="h-full w-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML = `<span class="text-xs font-bold text-white">${user?.name?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || 'U'}</span>`;
                      }}
                    />
                  ) : (
                    <span className="text-xs font-bold text-white">
                      {user?.name?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || 'U'}
                    </span>
                  )}
                </div>
                <div className="hidden xl:block">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100 max-w-32 truncate">
                    {user?.name || 'User'}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 max-w-32 truncate">
                    {user?.email}
                  </p>
                </div>
                <button
                  onClick={logout}
                  className="p-2 text-gray-400 hover:text-danger-500 transition-colors"
                  title="Logout"
                >
                  <LogOut className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Header with Menu */}
      <header className="lg:hidden fixed top-0 left-0 right-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 z-30">
        <div className="flex items-center justify-between px-4 py-2">
          <AppLogo size="small" showImage={true} />

          <div className="flex items-center space-x-3">
            <button
              onClick={toggleTheme}
              className="p-1.5 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              {isDark ? (
                <Sun className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              ) : (
                <Moon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              )}
            </button>

            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-1.5 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              {isMobileMenuOpen ? (
                <X className="h-6 w-6 text-gray-600 dark:text-gray-300" />
              ) : (
                <Menu className="h-6 w-6 text-gray-600 dark:text-gray-300" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        {isMobileMenuOpen && (
          <div className="absolute top-full left-0 right-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-lg p-4 animate-fade-in z-50">
            <div className="space-y-4">
              {/* User Info */}
              <div className="flex items-center space-x-3 px-2 py-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <div className="h-10 w-10 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-full flex items-center justify-center shadow-sm overflow-hidden">
                  {user?.profile_picture ? (
                    <img
                      src={user.profile_picture}
                      alt={user?.name || 'User'}
                      className="h-full w-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML = `<span class="text-sm font-bold text-white">${user?.name?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || 'U'}</span>`;
                      }}
                    />
                  ) : (
                    <span className="text-sm font-bold text-white">
                      {user?.name?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || 'U'}
                    </span>
                  )}
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {user?.name || 'User'}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {user?.email}
                  </p>
                </div>
              </div>

              {/* Secondary Navigation Items */}
              <div className="space-y-1">
                {secondaryNav.map((item) => {
                  const Icon = item.icon
                  const isActive = location.pathname === item.href
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={`
                          flex items-center space-x-3 px-3 py-3 rounded-lg text-sm font-medium transition-colors
                          ${isActive
                          ? 'bg-primary-50 text-primary-600 dark:bg-primary-900/20 dark:text-primary-400'
                          : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
                        }
                        `}
                    >
                      <Icon className="h-5 w-5" />
                      <span>{item.name}</span>
                    </Link>
                  )
                })}

                <button
                  onClick={logout}
                  className="w-full flex items-center space-x-3 px-3 py-3 rounded-lg text-sm font-medium text-danger-600 hover:bg-danger-50 dark:text-danger-400 dark:hover:bg-danger-900/20 transition-colors"
                >
                  <LogOut className="h-5 w-5" />
                  <span>Logout</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Main content with bottom padding for mobile nav */}
      <main
        className="flex-1 lg:pb-0 pt-14 lg:pt-0"
        style={{
          paddingBottom: window.innerWidth < 1024 ? 'calc(56px + max(env(safe-area-inset-bottom), 8px))' : '0'
        }}
      >
        <Outlet />
      </main>

      {/* Mobile Bottom Navigation (Primary Only) */}
      <nav
        className="lg:hidden fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 z-30"
        style={{
          paddingBottom: 'max(env(safe-area-inset-bottom), 8px)'
        }}
      >
        <div className="grid grid-cols-3 h-14">
          {primaryNav.map((item) => {
            const Icon = item.icon
            const isActive = location.pathname === item.href

            return (
              <Link
                key={item.name}
                to={item.href}
                className={`
                  flex flex-col items-center justify-center space-y-0.5 py-1 px-1 transition-all duration-200 min-h-[44px]
                  ${isActive
                    ? 'text-primary-600 dark:text-primary-400'
                    : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                  }
                `}
              >
                <div className={`p-1 rounded-full ${isActive ? 'bg-primary-50 dark:bg-primary-900/20' : ''}`}>
                  <Icon className={`h-5 w-5 ${isActive ? 'text-primary-600 dark:text-primary-400' : ''}`} />
                </div>
                <span className={`text-[10px] font-medium leading-none ${isActive ? 'text-primary-600 dark:text-primary-400' : ''}`}>
                  {item.shortName}
                </span>
              </Link>
            )
          })}
        </div>
      </nav>
    </div>
  )
}

export default Layout
